import {
  IFile,
  IFileState,
  IFileWithConsumersUpdated,
  IFilesTags,
  ISavedFilter,
} from "@tedivo/tvd-api-models";
import {
  IOptionsStorage,
  ISmartTableFilterLabels,
  ITable,
  ITableField,
  ITableOnClickOptions,
  SmartTable,
} from "@tedivo/tedivo-smart-table";
import {
  ISortByField,
  formatDateInCurrentTimezone,
} from "@tedivo/tedivo-pure-helpers";
import { getTranslation, i18n } from "../../../app/i18/i18tn";

import ICustomError from "../../types/ICustomError";
import { IntegratedDialog } from "@tedivo/tedivo-ui";
import Services from "../../../app/services";
import SlDialog from "@shoelace-style/shoelace/dist/components/dialog/dialog";
import SlSelect from "@shoelace-style/shoelace/dist/components/select/select";
import { createModalToForNotes } from "../view-json/parts/createModalToForNotes";
import { createModalToTagFiles } from "../view-json/parts/createModalToTagFiles";
import { createMtShipaNameDiffersDialog } from "./createMtShipaNameDiffersDialog";
import { removeChildren } from "@tedivo/tedivo-dom-helpers";
import { routeFns } from "../../../app/router/routes";
import router from "../../../app/router";
import showCGsCell from "./showCGsCell";
import { showFileHistory } from "../view-json/fileHistory/showFileHistory";

export type IFileDisplay = IFileWithConsumersUpdated & { tagsStr: string };

export function createFilesList({
  data = [],
  fileTags,
  organizationUsersBySub,
  onDelete,
  onOpen,
  onSort,
  onClone,
  onRefreshList,
  onTagsUpdated,
  onTagSelected,
  onSizeChange,
  onColumnSelectorChange,
  sortBy,
  actionsDropdown,
  dialogHistory,
  dialogTagFile,
  dialogFileNotes,
  isReadOnly,
  initialTagFilter,
  initialColumnsVisible,
  canShowDeleteButton,
  userOptionsStorageInstance,
}: {
  data: IFileDisplay[];
  fileTags: IFilesTags | undefined;
  organizationUsersBySub: Record<string, string> | undefined;
  onDelete: undefined | ((id: string, dta: IFile) => void);
  onClone: undefined | ((dta: IFile) => void);
  onRefreshList: undefined | (() => Promise<void>);
  onOpen: (id: string) => void;
  onSort: (sortFields: ISortByField<IFileDisplay>[]) => void;

  onTagsUpdated:
  | undefined
  | ((fileTags: IFilesTags, table: SmartTable<IFileDisplay>) => void);
  onTagSelected: undefined | ((tag: string) => void);
  onColumnSelectorChange:
  | undefined
  | ((fields: (keyof IFileDisplay)[]) => void);
  onError: undefined | ((error: ICustomError) => void);
  onSizeChange?: ITable<IFileDisplay>["onSizeChange"];
  sortBy: ISortByField<IFileDisplay>;
  actionsDropdown: HTMLElement | undefined;
  dialogHistory: SlDialog;
  dialogTagFile: IntegratedDialog<"ok"> | undefined;
  dialogFileNotes: IntegratedDialog<"ok"> | undefined;
  isReadOnly: boolean;
  initialTagFilter?: string;
  initialColumnsVisible?: (keyof IFileDisplay)[];
  canShowDeleteButton?: boolean;
  userOptionsStorageInstance: IOptionsStorage<IFileDisplay, ISavedFilter>;
}): HTMLElement {
  const isOwnCloud = !!onDelete;
  let internalFileTags = fileTags || { tags: {}, filesByTag: {} };
  let selectTagNode: SlSelect | undefined;
  let columnsVisible: (keyof IFileDisplay)[] = initialColumnsVisible || [];

  const nn = new Intl.NumberFormat(i18n.language);

  const fields: Array<ITableField<IFileDisplay> | undefined> = [
    {
      name: "name",
      label: getTranslation("general:common:name"),
    },
    {
      name: "imoCode",
      label: getTranslation("view:imoCode"),
    },
    {
      name: "callSign",
      label: getTranslation("view:callSign"),
    },
    {
      name: "shipClass",
      label: getTranslation("view:shipClass"),
      isList: true,
    },
    {
      name: "cgsPercentage",
      label: getTranslation("view:cgsPercentage"),
      className: "right-aligned",
      type: "number",
      mapper: showCGsCell,
      fixedWidth: "100px",
    },
    {
      name: "hasHatchCovers",
      type: "boolean",
      label: getTranslation("view:hasHatchCovers"),
      valueFunction: (dta) =>
        dta.hasHatchCovers
          ? getTranslation("general:common.yes")
          : getTranslation("general:common.no"),
      className: "centered",
      fixedWidth: "100px",
    },
    {
      name: "teus",
      label: "TEUs",
      type: "number",
      valueFunction: (dta) => (dta.teus ? nn.format(dta.teus) : "N/A"),
      className: "centered",
      fixedWidth: "100px",
    },
    {
      name: "state",
      label: getTranslation("general:fileState"),
      valueFunction: (dta) =>
        getTranslation(`enums:FileStateEnum.${dta.state}`),
      className: "centered",
      isList: true,
    },
    {
      name: "ovdValidationExportStafError",
      label: getTranslation("view:ovdValidStafReady"),
      valueFunction: (dta) =>
        !dta.ovdValidationExportStafError
          ? getTranslation("general:common.yes")
          : getTranslation("general:common.no"),
      className: (dta) =>
        `${!dta?.ovdValidationExportStafError ? "centered" : "warning-text centered"}`
    },
    isOwnCloud
      ? {
        name: "userSub",
        label: getTranslation("general:common.author"),
        valueFunction: (dta) =>
          dta.fromBvoName
            ? getTranslation("general:bvo")
            : organizationUsersBySub?.[dta.userSub] || "-",
        className: "centered",
        isList: true,
      }
      : undefined,
    isOwnCloud
      ? {
        name: "lastAuthor",
        label: getTranslation("general:common.modifiedBy"),
        valueFunction: (dta) => lastUpdatedByFn(dta, organizationUsersBySub),
        className: "centered",
        isList: true,
      }
      : undefined,
    {
      name: "lastModified",
      label: getTranslation("general:common:lastUpdated"),
      valueFunction: (dta) => formatDateInCurrentTimezone(dta.lastModified),
      className: "centered",
    },

    isOwnCloud
      ? {
        name: "tagsStr",
        label: getTranslation("general:fileTags.tags"),
        fixedWidth: "300px",
      }
      : undefined,
    {
      name: "fileId",
      label: isOwnCloud
        ? getTranslation("general:common.edit")
        : getTranslation("general:common.view"),
      type: "icon",
      icon: isOwnCloud ? "pencil" : "eye",
      notSortable: true,
      fixedWidth: "40px",
      smallPadding: true,
      onClick: (dta) => {
        onOpen(dta.fileId);
      },
    },
    isOwnCloud
      ? {
        name: "fileId",
        label: getTranslation("general:common.actions"),
        className: "files-actions",
        type: "actions",
        iconVariant: (dta) =>
          dta.cloneDiffers || dta.consumersUpdates || dta.vmdShipNameDiffers
            ? "warning"
            : "primary",
        icon: "three-dots-vertical",
        notSortable: true,
        notFilterable: true,
        fixedWidth: "40px",
        smallPadding: true,
        onClick: (dta, name, table, value) => {
          let node: HTMLElement | undefined;
          switch (value) {
            case "duplicate":
              if (onClone) onClone(dta);
              break;
            case "delete":
              onDelete(dta.fileId, dta);
              break;
            case "fileHistory":
              removeChildren(dialogHistory);

              node = showFileHistory({
                fileId: dta.fileId,
                organizationId: dta.organizationId,
                createdAt: dta.createdAt,
                updatedAt: dta.lastModified,
                userSub: dta.userSub,
                updatedBy: dta.lastAuthor,
                name: dta.name,
                fromBvoName: dta.fromBvoName,
                lastComment: dta.lastComment,
              });

              if (node) dialogHistory.appendChild(node);
              dialogHistory.label = `${getTranslation(
                "view:edit.fileHistory.title",
              )
                }: ${dta.name} `;

              dialogHistory.show();
              break;
            case "viewTvlMap":
              router.navigate(routeFns.fileMap(dta.fileId));
              break;
            case "manageTags":
              if (dialogTagFile)
                createModalToTagFiles({
                  fileId: dta.fileId,
                  fileName: dta.name,
                  modal: dialogTagFile,
                  filesTags: internalFileTags,
                  onUpdated: (fileTags) => {
                    if (fileTags) {
                      internalFileTags = fileTags;

                      if (onTagsUpdated) onTagsUpdated(fileTags, table);

                      updateTagsFilter(
                        initialTagFilter,
                        internalFileTags,
                        selectTagNode,
                      );
                    }
                  },
                });
              break;
            case "viewNotes":
              if (dialogFileNotes)
                createModalToForNotes({
                  organizationId: dta.organizationId,
                  fileId: dta.fileId,
                  fileName: dta.name,
                  modal: dialogFileNotes,
                  organizationUsersBySub,
                  onUpdated: undefined,
                });
              break;
            case "vmdShipNameDiffers":
              createMtShipaNameDiffersDialog({
                fileData: {
                  name: dta.name,
                  fileId: dta.fileId,
                  organizationId: dta.organizationId,
                  vmdShipNameLatest: dta.vmdShipNameLatest || "",
                },
                goSquaredLabel: "view_shipNameDiffers",
                onAction: async (action: "replace" | "ignore") => {
                  await Services.files.updateVmdShipNameDiffers(
                    dta.fileId,
                    action,
                  );
                  await onRefreshList?.();
                },
              });
              break;
          }
        },
        options: (d: IFileDisplay) =>
          [
            {
              label: getTranslation("general:fileTags.tags"),
              value: "manageTags",
              icon: "tags",
            },
            {
              label: getTranslation("view:edit.fileHistory.title"),
              value: "fileHistory",
              icon: "binoculars",
            },
            {
              label: getTranslation("general:fileNotes.fileNotes"),
              value: "viewNotes",
              icon: "stickies",
            },
            {
              label: getTranslation("view:fileMap.tvlMap"),
              value: "viewTvlMap",
              icon: "geo",
              variant: d.cloneDiffers || d.consumersUpdates ? "warn" : "",
            },
            isReadOnly
              ? undefined
              : d.vmdShipNameDiffers && d.vmdShipNameLatest
                ? {
                  label: getTranslation("view:shipNameDiffers.title"),
                  value: "vmdShipNameDiffers",
                  icon: "app-indicator",
                  variant: "warn",
                }
                : undefined,
            isReadOnly
              ? undefined
              : onClone
                ? {
                  label: getTranslation("general:common.duplicate"),
                  value: "duplicate",
                  icon: "file-earmark-plus",
                }
                : undefined,
            isReadOnly
              ? undefined
              : canShowDeleteButton
                ? {
                  label: getTranslation("general:common.delete"),
                  value: "delete",
                  icon: "trash",
                  variant: "danger",
                }
                : undefined,
          ].filter(Boolean) as ITableOnClickOptions[],
      }
      : undefined,
  ];

  const filesCount = data.reduce(
    (acc, f) => {
      acc[f.state]++;
      return acc;
    },
    { DRAFT: 0, ORG_READY: 0, COMMUNITY: 0 } as Record<IFileState, number>,
  );

  const filesCountStr = (["DRAFT", "ORG_READY", "COMMUNITY"] as IFileState[])
    .reduce((acc, state) => {
      if (filesCount[state]) {
        acc.push(
          `${getTranslation(`enums:FileStateEnum.${state}`)} ${filesCount[state]
          } `,
        );
      }
      return acc;
    }, [] as string[])
    .join(" / ");

  const tableTitle = `${isOwnCloud
    ? getTranslation("menu:myCloud")
    : getTranslation("menu:theirCloud")
    } <small class="filesCount" > (${filesCountStr})</small>`;

  const table = new SmartTable<IFileDisplay>().initialize({
    className: "tvd-table tvd-table-smart-table tvd-table-sticky-caption",
    title: tableTitle,
    data,
    defaultSortBy: {
      name: sortBy.name,
      ascending: sortBy.ascending,
    },
    fields: fields.filter(Boolean) as ITableField<IFileDisplay>[], // Remove undefined fields
    settings: {
      sortEnabled: true,
      sortVisible: true,
      userOptionsStorage: userOptionsStorageInstance,
      filters: {
        textFieldEnabled: true,
        textFieldFilterFields: ["name", "imoCode", "callSign", "shipClass"],
        initialTextFieldFilter: userOptionsStorageInstance.retrieveFilterText(),
        advancedEnabled: true,
        advancedLabels: createFilterLabels(),
        advancedDialogPrettyHtml: false,
        advancedEnableStoring: true,
        initialTreeFilter:
          userOptionsStorageInstance.retrieveFilterTree(undefined),
      },
      labelSearch: getTranslation("general:common.filterBy"),
      labelSortBy: getTranslation("general:common.sortBy"),
      createSortIconFn: createSortIcon,
      actionBox: actionsDropdown,
      columnSelector: true,
      columnSelectorFixed: ["name", "state", "fileId"],
      columnSelectorButtonText: getTranslation("general:common.ok"),
      columnSelectorHelp: getTranslation("general:common.selectColumns"),
      onColumnSelectorChange: (fields) => {
        columnsVisible = fields;
        if (onColumnSelectorChange) onColumnSelectorChange(fields);
      },
    },

    pkFunction: (dta) => dta.fileId,
    onSortParamsChanged: onSort,
    onSizeChange,
    rowClassFunction: (dta) => {
      if (!dta.imoCode || !dta.callSign) return "warning";
      return undefined;
    },
  });

  table.columnsVisible = columnsVisible || [];

  // Add tags filter
  if (fileTags) {
    const { tooltip, selectTag } = addTagsFilter(
      internalFileTags,
      table,
      onTagSelected,
      initialTagFilter,
    );

    selectTag.addEventListener("sl-change", (e) => {
      const tag = (e.target as SlSelect).value as string;
      table.restrictedToPks = internalFileTags.filesByTag[tag] ?? [];
      window.setTimeout(() => {
        if (tag) selectTag.focus();
      }, 500);
      if (onTagSelected) onTagSelected(tag);
    });

    selectTagNode = selectTag;

    table.appendToHeaderPseudoSlot(tooltip, false);

    if (initialTagFilter) {
      table.restrictedToPks =
        internalFileTags.filesByTag[initialTagFilter] ?? [];
      initialTagFilter = "";
    }
  }

  return table;
}

function addTagsFilter(
  internalFileTags: IFilesTags,
  table: SmartTable<IFileDisplay>,
  onTagSelected: undefined | ((tag: string) => void),
  initialTagFilter: string | undefined,
) {
  const tooltip = document.createElement("sl-tooltip");
  tooltip.content = getTranslation("general:fileTags.tagHelp");

  const selectTag = document.createElement("sl-select");
  selectTag.title = getTranslation("general:fileTags.tags");
  selectTag.placeholder = getTranslation("general:fileTags.tagFilter");
  selectTag.clearable = true;
  selectTag.filled = false;
  selectTag.style.textAlign = "left";
  selectTag.style.maxWidth = "200px";
  selectTag.defaultValue = "";

  updateTagsFilter(initialTagFilter, internalFileTags, selectTag);

  const iconTags = document.createElement("sl-icon");
  iconTags.name = "tags";
  iconTags.slot = "prefix";
  selectTag.appendChild(iconTags);

  tooltip.appendChild(selectTag);

  return { selectTag, tooltip };
}

function updateTagsFilter(
  tag: string | undefined,
  internalFileTags: IFilesTags,
  selectTag: SlSelect | undefined,
) {
  if (!selectTag) return;

  removeChildren(selectTag);

  const tags = Object.keys(internalFileTags.tags)
    .filter((tag) => internalFileTags.filesByTag[tag]?.length > 0)
    .sort();

  if (tags.length > 0) {
    tags.forEach((tag) => {
      const option = document.createElement("sl-option");
      option.value = tag;
      option.innerHTML = internalFileTags.tags[tag];
      selectTag.appendChild(option);
    });
  }

  if (tag && tags.includes(tag)) selectTag.value = tag;
}

function lastUpdatedByFn(
  dta: IFileDisplay,
  organizationUsersBySub: Record<string, string> | undefined,
): string {
  const la = dta.lastAuthor
    ? organizationUsersBySub?.[dta.lastAuthor]
    : undefined;

  // Get initials only of last author
  if (la) {
    const names = la.split(" ");
    return names.reduce(
      (acc, curr, idx) => acc + (idx === 0 ? curr + " " : curr.charAt(0) + "."),
      "",
    );
  }

  return "-";
}

function createSortIcon(asc: boolean) {
  const icon = document.createElement("sl-icon");
  icon.name = asc ? "sort-down-alt" : "sort-up-alt";
  return icon;
}

function createFilterLabels(): ISmartTableFilterLabels {
  const filterByColumnLabels: ISmartTableFilterLabels = {
    addAndSibling: getTranslation("filters:addAndSibling"),
    addOrSibling: getTranslation("filters:addOrSibling"),
    and: getTranslation("filters:and"),
    booleanFalseValue: getTranslation("filters:booleanFalseValue"),
    booleanTrueValue: getTranslation("filters:booleanTrueValue"),
    buttonLabel: getTranslation("filters:buttonLabel"),
    cancel: getTranslation("filters:cancel"),
    clear: getTranslation("filters:clear"),
    close: getTranslation("general:common.close"),
    createAndGroup: getTranslation("filters:createAndGroup"),
    createOrGroup: getTranslation("filters:createOrGroup"),
    deleteCondition: getTranslation("filters:deleteCondition"),
    deleteFilter: getTranslation("filters:deleteFilter"),
    editName: getTranslation("filters:editName"),
    filterNameAlreadyExists: getTranslation("filters:filterNameAlreadyExists"),
    filterNameUnique: getTranslation("filters:filterNameUnique"),
    filterPreviewTitle: getTranslation("filters:filterPreviewTitle"),
    filterSettings: getTranslation("filters:filterSettings"),
    manageFilters: getTranslation("filters:manageFilters"),
    name: getTranslation("general:common.name"),
    none: getTranslation("general:common.none"),
    or: getTranslation("filters:or"),
    replaceFilter: getTranslation("filters:replaceFilter"),
    save: getTranslation("filters:save"),
    saveChange: getTranslation("filters:saveChange"),
    saveFilter: getTranslation("filters:saveFilter"),
    selectField: getTranslation("filters:selectField"),
    selectOperator: getTranslation("filters:selectOperator"),
    selectStoredFilter: getTranslation("filters:selectStoredFilter"),
    selectValue: getTranslation("filters:selectValue"),
    storeFilter: getTranslation("filters:storeFilter"),
    storeFilterHelpText: getTranslation("filters:storeFilterHelpText"),
    title: getTranslation("filters:title"),
    operators: {
      StringFilterOperatorEnum: {
        EQUALS: getTranslation(
          "filters:operators.StringFilterOperatorEnum.EQUALS",
        ),
        CONTAINS: getTranslation(
          "filters:operators.StringFilterOperatorEnum.CONTAINS",
        ),
        STARTS_WITH: getTranslation(
          "filters:operators.StringFilterOperatorEnum.STARTS_WITH",
        ),
        ENDS_WITH: getTranslation(
          "filters:operators.StringFilterOperatorEnum.ENDS_WITH",
        ),
      },
      NumberFilterOperatorEnum: {
        CONTAINS: getTranslation(
          "filters:operators.NumberFilterOperatorEnum.CONTAINS",
        ),
        EQUALS: getTranslation(
          "filters:operators.NumberFilterOperatorEnum.EQUALS",
        ),
        GREATER_THAN: getTranslation(
          "filters:operators.NumberFilterOperatorEnum.GREATER_THAN",
        ),
        LESS_THAN: getTranslation(
          "filters:operators.NumberFilterOperatorEnum.LESS_THAN",
        ),
        GREATER_THAN_OR_EQUALS: getTranslation(
          "filters:operators.NumberFilterOperatorEnum.GREATER_THAN_OR_EQUALS",
        ),
        LESS_THAN_OR_EQUALS: getTranslation(
          "filters:operators.NumberFilterOperatorEnum.LESS_THAN_OR_EQUALS",
        ),
      },
      ListFilterOperatorEnum: {
        IN: getTranslation("filters:operators.ListFilterOperatorEnum.IN"),
        NOT_IN: getTranslation(
          "filters:operators.ListFilterOperatorEnum.NOT_IN",
        ),
      },
    },
  };
  return filterByColumnLabels;
}
